import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, Form, Input, Modal, Typography } from "antd";
import { LockOutlined } from "@mui/icons-material";

import { userPutChangePassword } from "@apis";
import { useToaster } from "@components";
import { LogoutSystem } from "@helpers";

const { Text } = Typography;

/**
 * Modal bloqueante que se muestra en el primer ingreso del usuario
 * (cuando el backend devuelve user.mustChangePassword = true).
 * No se puede cerrar ni esquivar: al cambiar la clave se fuerza re-login.
 */
export const ForceChangePasswordModal = () => {
  const toaster = useToaster();
  const [processing, setProcessing] = React.useState(false);

  const formik = useFormik({
    initialValues: { backPass: "", newPass: "", confPass: "" },
    validationSchema: Yup.object({
      backPass: Yup.string().trim().min(2).max(20).required("Requerido"),
      newPass: Yup.string().trim().min(2).max(20).required("Requerido"),
      confPass: Yup.string().trim().min(2).max(20).required("Requerido"),
    }),
    onSubmit: async (values) => {
      if (values.newPass !== values.confPass) {
        toaster.error({
          title: "Error",
          message: "Las claves no coinciden",
          duration: 4000,
          placement: "topRight",
        });
        return;
      }

      if (values.newPass === values.backPass) {
        toaster.error({
          title: "Error",
          message: "La nueva clave debe ser distinta a la actual",
          duration: 4000,
          placement: "topRight",
        });
        return;
      }

      try {
        setProcessing(true);
        const { confPass, ...rest } = values;
        await userPutChangePassword(rest);
        toaster.success({
          title: "Clave actualizada",
          message: "Vuelve a iniciar sesión con tu nueva contraseña",
          duration: 5000,
          placement: "topRight",
        });
        setTimeout(() => LogoutSystem(), 1200);
      } catch (err: any) {
        toaster.error({
          title: "Error",
          message: err?.message ?? "No se pudo cambiar la clave",
          duration: 5000,
          placement: "topRight",
        });
        setProcessing(false);
      }
    },
  });

  return (
    <Modal
      open
      title="Cambia tu contraseña"
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      footer={null}
    >
      <Text type="secondary">
        Es tu primer ingreso. Por seguridad, debes reemplazar la contraseña
        temporal que recibiste por correo antes de continuar.
      </Text>

      <Form layout="vertical" style={{ marginTop: 20 }}>
        <Form.Item
          label="Contraseña actual"
          validateStatus={
            formik.touched.backPass && formik.errors.backPass ? "error" : ""
          }
          help={formik.touched.backPass && formik.errors.backPass}
        >
          <Input.Password
            placeholder="Contraseña temporal"
            prefix={<LockOutlined style={{ color: "#aaa", fontSize: 16 }} />}
            name="backPass"
            value={formik.values.backPass}
            onChange={formik.handleChange}
            onBlur={(e) =>
              formik.setFieldValue("backPass", e.target.value.trim())
            }
            disabled={processing}
          />
        </Form.Item>

        <Form.Item
          label="Nueva contraseña"
          validateStatus={
            formik.touched.newPass && formik.errors.newPass ? "error" : ""
          }
          help={formik.touched.newPass && formik.errors.newPass}
        >
          <Input.Password
            placeholder="Nueva contraseña"
            prefix={<LockOutlined style={{ color: "#aaa", fontSize: 16 }} />}
            name="newPass"
            value={formik.values.newPass}
            onChange={formik.handleChange}
            onBlur={(e) =>
              formik.setFieldValue("newPass", e.target.value.trim())
            }
            disabled={processing}
          />
        </Form.Item>

        <Form.Item
          label="Confirmar nueva contraseña"
          validateStatus={
            formik.touched.confPass && formik.errors.confPass ? "error" : ""
          }
          help={formik.touched.confPass && formik.errors.confPass}
        >
          <Input.Password
            placeholder="Repite la nueva contraseña"
            prefix={<LockOutlined style={{ color: "#aaa", fontSize: 16 }} />}
            name="confPass"
            value={formik.values.confPass}
            onChange={formik.handleChange}
            onBlur={(e) =>
              formik.setFieldValue("confPass", e.target.value.trim())
            }
            onPressEnter={() => formik.handleSubmit()}
            disabled={processing}
          />
        </Form.Item>

        <Button
          type="primary"
          block
          loading={processing}
          onClick={() => formik.handleSubmit()}
        >
          Cambiar y volver a iniciar sesión
        </Button>
      </Form>
    </Modal>
  );
};
